import {useMemo} from 'react';
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import type {K1Page} from '@/pages/components/audit-program/k-section/constants';
import {K1_PAGE} from '@/pages/components/audit-program/k-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface K1LeadViewProps {
    header: AuditProgramSectionVO;
    page: K1Page;
    refCode?: string;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const cellBorder = {borderColor: 'divider'};
const headSx = {...cellBorder, fontWeight: 600, fontSize: '0.75rem', py: 0.75, px: 0.5};
/** Contain InputField default minWidth:110 so adjacent cells don't overlap */
const bodySx = {...cellBorder, py: 0.45, px: 0.4, verticalAlign: 'top' as const, overflow: 'hidden'};
const fieldSx = {minWidth: 0, width: '100%', maxWidth: '100%'};

/** K1 — Bank balance lead schedule (account list). */
export default function K1LeadView({
    header,
    page,
    refCode = 'K1',
    values,
    onChange,
}: K1LeadViewProps) {
    const meta = {
        title: page.title || K1_PAGE.title,
        cyLabel: page.cyLabel || K1_PAGE.cyLabel,
        lyLabel: page.lyLabel || K1_PAGE.lyLabel,
        blankAccountRows: page.blankAccountRows || K1_PAGE.blankAccountRows,
        remarksTitle: page.remarksTitle || K1_PAGE.remarksTitle,
        remarks: page.remarks?.length ? page.remarks : K1_PAGE.remarks,
    };
    const rowIds = useMemo(
        () => Array.from({length: Math.max(1, meta.blankAccountRows)}, (_, i) => `a${i + 1}`),
        [meta.blankAccountRows],
    );

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a12"
                refCode={refCode}
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.3, mb: 1.5, mt: 0.5}}
            >
                {meta.title}
            </Typography>

            <TableContainer sx={{mb: 2, maxWidth: 780}}>
                <Table size="small" sx={{tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <colgroup>
                        <col/>
                        <col style={{width: 56}}/>
                        <col style={{width: 140}}/>
                        <col style={{width: 48}}/>
                        <col style={{width: 140}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx}>Bank account</TableCell>
                            <TableCell sx={headSx}/>
                            <TableCell sx={headSx} align="right">{meta.cyLabel}</TableCell>
                            <TableCell sx={headSx}/>
                            <TableCell sx={headSx} align="right">{meta.lyLabel}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rowIds.map((id) => (
                            <TableRow key={id}>
                                <TableCell sx={bodySx}>
                                    <InputField
                                        type="text"
                                        fullWidth
                                        compact
                                        sx={fieldSx}
                                        value={values[`${id}.name`] ?? ''}
                                        onChange={(e) => onChange(`${id}.name`, e.target.value)}
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <InputField
                                        type="text"
                                        fullWidth
                                        compact
                                        align="center"
                                        sx={fieldSx}
                                        value={values[`${id}.tick`] ?? ''}
                                        onChange={(e) => onChange(`${id}.tick`, e.target.value)}
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <InputField
                                        type="text"
                                        fullWidth
                                        compact
                                        align="right"
                                        sx={fieldSx}
                                        value={values[`${id}.cy`] ?? ''}
                                        onChange={(e) => onChange(`${id}.cy`, e.target.value)}
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <InputField
                                        type="text"
                                        fullWidth
                                        compact
                                        align="center"
                                        sx={fieldSx}
                                        value={values[`${id}.cyMark`] ?? ''}
                                        onChange={(e) => onChange(`${id}.cyMark`, e.target.value)}
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <InputField
                                        type="text"
                                        fullWidth
                                        compact
                                        align="right"
                                        sx={fieldSx}
                                        value={values[`${id}.ly`] ?? ''}
                                        onChange={(e) => onChange(`${id}.ly`, e.target.value)}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.5}}>
                {meta.remarksTitle}
            </Typography>
            <Stack spacing={0.25}>
                {meta.remarks.map((r, idx) => (
                    <Typography key={`${r.tick}-${idx}`} variant="body2" sx={{pl: 0.5, lineHeight: 1.4}}>
                        <Box component="span" sx={{display: 'inline-block', minWidth: 44, fontWeight: 600}}>
                            {r.tick}
                        </Box>
                        {r.text}
                    </Typography>
                ))}
            </Stack>
        </Box>
    );
}
